/**
 * Build a `newCompany` sub-object from the required default+custom field
 * inputs. Returns null when no field has a value (so the caller can omit
 * the property entirely).
 *
 * @param {Array<Object>} requiredDefs - field definitions with isRequired === true
 * @param {Object} fieldValues - map of fieldKey/fieldId → user input
 * @returns {Object|null}
 */
function buildNewCompanyData(requiredDefs, fieldValues) {
	const nc = {};
	const ncCustom = {};
	for (const def of requiredDefs) {
		const val = fieldValues[def.isDefault ? def.fieldKey : def.id];
		if (val === null || val === '') continue;
		if (def.isDefault) nc[def.fieldKey] = val;
		else ncCustom[def.id] = val;
	}
	if (Object.keys(ncCustom).length > 0) nc.customFields = ncCustom;
	return Object.keys(nc).length > 0 ? nc : null;
}

/**
 * Compose the POST payload for creating a deal.
 *
 * @param {Object} args
 * @param {string} args.title
 * @param {string} args.companyName
 * @param {Object|null} args.selectedCompany - matched CRM company or null
 * @param {string} args.pipelineId
 * @param {string} args.stageId
 * @param {string} args.dealValue - numeric string from input (may be empty)
 * @param {string} args.expectedCloseAt - YYYY-MM-DD (may be empty)
 * @param {string} args.assignedUserId
 * @param {string} args.description
 * @param {Object} args.customFields - { fieldId: value, ... }
 * @param {boolean} args.isNewCompany - true when user typed a name without selecting
 * @param {Array<Object>} args.newCompanyRequiredDefs
 * @param {Object} args.newCompanyFields
 * @returns {Object}
 */
export function buildDealPayload({
	title,
	companyName,
	selectedCompany,
	pipelineId,
	stageId,
	dealValue,
	expectedCloseAt,
	assignedUserId,
	description,
	customFields,
	isNewCompany,
	newCompanyRequiredDefs,
	newCompanyFields,
}) {
	const cf = Object.fromEntries(Object.entries(customFields).filter(([, v]) => v !== '' && v !== undefined));
	const payload = {
		title: title.trim(),
		companyNameSnapshot: companyName,
		crmCompanyId: selectedCompany?.id ?? null,
		pipelineId,
		stageId,
		dealValue: dealValue ? Number(dealValue) : null,
		expectedCloseAt: expectedCloseAt || null,
		userId: assignedUserId || null,
		description: description.trim() || null,
		sourceType: 'manual',
		...(Object.keys(cf).length > 0 ? { customFields: cf } : {}),
	};

	if (isNewCompany && newCompanyRequiredDefs.length > 0) {
		const newCompany = buildNewCompanyData(newCompanyRequiredDefs, newCompanyFields);
		if (newCompany) payload.newCompany = newCompany;
	}

	return payload;
}
